/**
 * Tab strip for the contact drawer's right panel.
 *
 * Same markup as `ContractorDetailsTabs` in the main project — white strip, 1px `#EDEDED` bottom
 * border, `h6`/`subtitle1` type swap on selection and the 3px black indicator driven by
 * `useTabIndicator`. Only the tab list differs.
 */
import { memo, useMemo } from 'react'
import tw from 'twin.macro'
import { ButtonBase, Typography } from '@mui/material'
import { colors } from 'src/theme'
import { useTabIndicator } from 'src/hooks/useTabIndicator'
import { ContactDrawerTab } from './types'

interface Props {
  selectedTab: ContactDrawerTab
  onClick(tab: ContactDrawerTab): void
}

const ContactDrawerTabsComponent = ({ selectedTab, onClick }: Props) => {
  const { containerRef, elementRefs, indicatorStyle } =
    useTabIndicator<ContactDrawerTab>(selectedTab)

  /**
   * Static titles. `useTabIndicator` only re-measures when `selectedTab` changes, so a title that
   * grows — a `(3)` counter appearing after a contact is logged — leaves the underline at its old
   * width. The sketch has no counter either.
   */
  const tabs = useMemo(
    (): Array<{ key: ContactDrawerTab; title: string }> => [
      { key: ContactDrawerTab.General, title: 'General' },
      { key: ContactDrawerTab.Contacts, title: 'External contacts' },
    ],
    [],
  )

  return (
    <div
      ref={containerRef}
      css={tw`relative bg-white border-0 border-b border-solid border-[#EDEDED]`}
    >
      <div role="tablist" css={tw`flex w-full overflow-hidden`}>
        {tabs.map((tab) => {
          const isSelected = selectedTab === tab.key

          return (
            /* `ButtonBase` rather than a styled div: it carries the button role, focusability and
               Enter/Space activation, which a div with an onClick does not. */
            <ButtonBase
              key={tab.key}
              role="tab"
              aria-selected={isSelected}
              data-testid={`contact-contractor-drawer-tab-${tab.key}`}
              css={tw`pt-4 px-4 pb-3 cursor-pointer transition-colors duration-300`}
              onClick={() => {
                onClick(tab.key)
              }}
            >
              <Typography
                variant={isSelected ? 'h6' : 'subtitle1'}
                ref={(el) => (elementRefs.current[tab.key] = el)}
                color={isSelected ? colors.BASICS_BLACK : colors.SHADES_GREY_500}
              >
                {tab.title}
              </Typography>
            </ButtonBase>
          )
        })}
      </div>
      <div
        css={[
          tw`absolute bottom-0 h-[3px] bg-black rounded-tl-[8px] rounded-tr-[8px] transition-all duration-300`,
          { left: indicatorStyle.left, width: indicatorStyle.width },
        ]}
      />
    </div>
  )
}

export const ContactDrawerTabs = memo(ContactDrawerTabsComponent)
